/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component, OnDestroy } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    UserService
} from '../../../../../core/services';
import { LockApiService, SystemApiService } from '../../../shared/http';
import { LockService, UpdatesService } from '../../../shared/services/utilities';
import { Observable, Subscriber } from 'rxjs';
import { Router } from '@angular/router';
import { UpdateModel } from '../../../shared/models';
import { concatMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-software-update',
    templateUrl: './systemUpdate.component.html',
    styleUrls: ['./systemUpdate.component.scss']
})

export class SystemUpdateComponent extends BaseComponent implements OnDestroy {
    /**
     * Packages to update info.
     */
    public updates: UpdateModel = {
        restartRequired: false,
        packages: [],
        majorVersion: [],
    };

    /**
     * Store the errors.
     */
    public error: any = {
        status: null,
        message: null
    };

    /**
     * To store the table rows.
     */
    public tableRows: any = [];

    /**
     * Is loading state.
     */
    public isLoading: boolean = true;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private loadingService: LoadingService,
        private lockApiService: LockApiService,
        private lockService: LockService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private systemApiService: SystemApiService,
        private updatesService: UpdatesService,
        private userService: UserService
    ) {
        super();

        this.registerButtons();
        this.setUpPageData();
        // Hide the update notification.
        this.notifyService.hideNotificationById('notify_system_update');
    }

    ngOnDestroy(): void {
        this.notifyService.showNotificationById('notify_system_update');
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Confirm online update.
     */
    public confirmMajorOnlineUpdate(row: any): void {
        const confirmationObject = {
            title: 'update',
            message: 'system_update_confirm_update_message',
            callBack: this.majorVersionUpdate.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm online update.
     */
    public confirmOnlineUpdate(): void {
        const confirmationObject = {
            title: 'update',
            message: 'system_update_confirm_update_message',
            callBack: this.updatePackages.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Restart Box.
     */
    public restart(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.systemApiService.restart()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.modalService.close('restartConfirm');
                        this.userService.logout(false);
                        this.router.navigateByUrl('/restart').then();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.loadingService.loadingButtonEmitter.next(false);
                        this.logService.error(
                            'SysControlComponent: restart',
                            'Error submitting reboot request',
                            error
                        );
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update the major version of the client OS.
     * @param row
     * @private
     */
    private majorVersionUpdate(row: { versionNumber: string; }): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.systemApiService.updateMajorVersion({ versionNumber: row.versionNumber })
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.updates = {
                            restartRequired: false,
                            packages: [],
                            majorVersion: [],
                        };
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update packages.
     * @private
     */
    private updatePackages(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.modalService.close('confirmation-box');
            this.modalService.openModalInParent('wait');

            // Force userLock to be true
            this.lockService.setLockStateByProperty('userLock', true);

            this.systemApiService.updatePackages({ timeout: 20000 })
                .pipe(
                    concatMap(() => this.getLockStatus()),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (): void => {
                        this.successCleanUp();
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.handleError(error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get the lock service.
     */
    private getLockStatus(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const intervalId = setInterval((): void => {
                const userLock: boolean = this.lockService.getLockStateByProperty('userLock');
                if (!userLock) {
                    clearInterval(intervalId);
                    observer.next();
                    observer.complete();
                }
            }, 2500);
        });
    }

    /**
     * Success clean up.
     * @private
     */
    private successCleanUp(): void {
        this.cleanupNotifications();
        this.notifyService.unmuteLocalNotifications();
        this.updates = {
            restartRequired: false,
            packages: [],
            majorVersion: [],
        };
        this.modalService.close('wait');
        this.notifyService.success('system_update_system_packages_up_to_date', { messageTranslate: true });
    }

    /**
     * Cleanup notifications.
     * @private
     */
    private cleanupNotifications(): void {
        this.notifyService.removeNotificationById('notify_system_update');
        this.notifyService.removeNotificationById('notifyMismatchWarning');
    }

    /**
     * Set up updates emitter subscriber.
     * @private
     */
    private setUpPageData(): void {
        const hasForcePermissions: any = this.permissionsLookup['system-update-force'];
        this.systemApiService.getUpdates({ force: true, hasForcePermissions: hasForcePermissions })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (updates: UpdateModel): void => {
                    this.isLoading = false;
                    if (updates.restartRequired) {
                        this.modalService.openModalInParent('restartConfirm');
                    }

                    this.updates = updates;
                },
                error: (error: ErrorResponseModel): void => {
                    this.error = error;
                }
            });

        this.updatesService.updatesEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (updates: UpdateModel): void => {
                    this.updates = updates;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SystemUpdateComponent:setUpPageData',
                        'Error getting data from the updatesService ' + error
                    );
                }
            });
    }

    /**
     * Handle error.
     * @param error
     * @private
     */
    private handleError(error: ErrorResponseModel): void {
        this.modalService.close('wait');
        this.notifyService.unmuteLocalNotifications();
        // Show the error on our page so we know
        if (error.message.includes('locked by update')) {
            const callBack = {
                text: 'force_unlock', function: this.confirmUnlock.bind(this)
            };
            this.notifyService.error(error.message, { id: 'update-error', callBack });
        } else {
            this.notifyService.error(error.message, { id: 'update-error' });
        }
    }

    /**
     * Confirm unlock modal.
     * @private
     */
    private confirmUnlock(): void {
        const confirmationObject = {
            title: 'update',
            message: 'system_update_confirm_unlock',
            callBack: this.unLockUserLock.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Unlock the user lock.
     * @private
     */
    private unLockUserLock(): Observable<any> {
        return new Observable(observer => {
            this.lockApiService.unLockUserLock()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.notifyService.removeNotificationById('update-error');
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'system-update-manual-update': [{ path: '/system/update', methods: ['POST'] }],
            'system-update-online-update-major': [{ path: '/system/update/majorversion', methods: ['PUT'] }],
            'system-update-online-update': [{ path: '/system/update', methods: ['PUT'] }],
            'system-update-force': [{ path: '/system/update/force', methods: ['GET'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
